import { useState } from 'react'
import { X, ArrowLeft, ArrowRight, Check, Building, User, FileText, CreditCard, Settings, Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { Stepper } from './ui/Stepper'
import { partnerAdminApi } from '../services/api'
import { useCNPJValidation, formatCNPJ, cleanCNPJ, validateCNPJChecksum } from '../hooks/useCNPJValidation'
import { Select } from './ui/Select'

interface ClientOnboardingWizardProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface ClientFormData {
  // Step 1: Dados Básicos
  name: string
  cnpj: string
  razaoSocial: string
  nomeFantasia: string

  // Step 2: Contato
  email: string
  phone: string
  responsavelNome: string
  responsavelCpf: string

  // Step 3: Endereço
  cep: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cidade: string
  estado: string

  // Step 4: Plano
  subscriptionPlan: 'basic' | 'professional' | 'enterprise'
  paymentMethod: 'boleto' | 'pix' | 'credit_card'
  monthlyFee: string

  // Step 5: Configurações
  speciality: string
  taxRegime: 'simples_nacional' | 'lucro_presumido' | 'lucro_real' | 'mei'
  sendWelcomeEmail: boolean
}

const initialFormData: ClientFormData = {
  name: '',
  cnpj: '',
  razaoSocial: '',
  nomeFantasia: '',
  email: '',
  phone: '',
  responsavelNome: '',
  responsavelCpf: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  subscriptionPlan: 'basic',
  paymentMethod: 'boleto',
  monthlyFee: '',
  speciality: '',
  taxRegime: 'simples_nacional',
  sendWelcomeEmail: true,
}

const steps = [
  { id: 'dados-basicos', title: 'Dados Básicos', description: 'Informações da empresa' },
  { id: 'contato', title: 'Contato', description: 'Dados de contato' },
  { id: 'endereco', title: 'Endereço', description: 'Localização' },
  { id: 'plano', title: 'Plano', description: 'Assinatura' },
  { id: 'configuracoes', title: 'Configurações', description: 'Preferências' },
]

export function ClientOnboardingWizard({ isOpen, onClose, onSuccess }: ClientOnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState<ClientFormData>(initialFormData)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // CNPJ Validation
  const cnpjValidation = useCNPJValidation()
  const [cnpjStatus, setCnpjStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid' | 'exists'>('idle')

  if (!isOpen) return null

  const updateFormData = (field: keyof ClientFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Handle CNPJ change with validation
  const handleCnpjChange = async (value: string) => {
    const cleaned = cleanCNPJ(value)
    const formatted = cleaned.length <= 14 ? formatCNPJ(cleaned) : formatCNPJ(cleaned.slice(0, 14))

    updateFormData('cnpj', formatted)
    setCnpjStatus('idle')
    cnpjValidation.reset()

    if (cleaned.length === 14) {
      if (!validateCNPJChecksum(cleaned)) {
        setCnpjStatus('invalid')
        return
      }

      setCnpjStatus('validating')

      const existsResult = await cnpjValidation.checkExists(cleaned)
      if (existsResult?.exists) {
        setCnpjStatus('exists')
        return
      }

      const fullResult = await cnpjValidation.validateFull(cleaned)
      if (fullResult) {
        if (fullResult.valid) {
          setCnpjStatus('valid')
          // Auto-fill data from Receita Federal
          if (fullResult.rfData) {
            if (fullResult.rfData.razaoSocial) {
              updateFormData('razaoSocial', fullResult.rfData.razaoSocial)
            }
            if (fullResult.rfData.nomeFantasia) {
              updateFormData('nomeFantasia', fullResult.rfData.nomeFantasia)
              if (!formData.name) {
                updateFormData('name', fullResult.rfData.nomeFantasia)
              }
            }
            if (fullResult.rfData.email) {
              updateFormData('email', fullResult.rfData.email)
            }
            if (fullResult.rfData.telefone) {
              updateFormData('phone', fullResult.rfData.telefone)
            }
            if (fullResult.rfData.endereco) {
              const end = fullResult.rfData.endereco
              updateFormData('logradouro', end.logradouro || '')
              updateFormData('numero', end.numero || '')
              updateFormData('complemento', end.complemento || '')
              updateFormData('bairro', end.bairro || '')
              updateFormData('cidade', end.municipio || '')
              updateFormData('estado', end.uf || '')
              updateFormData('cep', end.cep || '')
            }
          }
        } else {
          setCnpjStatus('invalid')
        }
      }
    }
  }

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    switch (step) {
      case 0: // Dados Básicos
        if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório'
        if (!formData.cnpj.trim()) newErrors.cnpj = 'CNPJ é obrigatório'
        else if (cnpjStatus === 'invalid') newErrors.cnpj = 'CNPJ inválido'
        else if (cnpjStatus === 'exists') newErrors.cnpj = 'CNPJ já cadastrado'
        break

      case 1: // Contato
        if (!formData.email.trim()) newErrors.email = 'Email é obrigatório'
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email inválido'
        if (!formData.phone.trim()) newErrors.phone = 'Telefone é obrigatório'
        if (!formData.responsavelNome.trim()) newErrors.responsavelNome = 'Nome do responsável é obrigatório'
        break

      case 2: // Endereço
        if (!formData.cep.trim()) newErrors.cep = 'CEP é obrigatório'
        if (!formData.logradouro.trim()) newErrors.logradouro = 'Logradouro é obrigatório'
        if (!formData.numero.trim()) newErrors.numero = 'Número é obrigatório'
        if (!formData.cidade.trim()) newErrors.cidade = 'Cidade é obrigatória'
        if (!formData.estado.trim()) newErrors.estado = 'Estado é obrigatório'
        break

      case 3: // Plano
        if (!formData.monthlyFee.trim()) newErrors.monthlyFee = 'Valor mensal é obrigatório'
        break

      case 4: // Configurações
        if (!formData.speciality.trim()) newErrors.speciality = 'Especialidade é obrigatória'
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(prev => prev + 1)
      } else {
        handleSubmit()
      }
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      setSubmitError(null)

      // Converter valor mensal de string "800,00" para número 800.00
      const monthlyFeeNumber = formData.monthlyFee
        ? parseFloat(formData.monthlyFee.replace(',', '.'))
        : 0

      const clientData = {
        name: formData.name,
        cnpj: cleanCNPJ(formData.cnpj),
        email: formData.email,
        phone: formData.phone,
        speciality: formData.speciality,
        subscriptionPlan: formData.subscriptionPlan,
        monthlyFee: monthlyFeeNumber,
        location: `${formData.cidade}, ${formData.estado}`,
        // Additional data for complete onboarding
        razaoSocial: formData.razaoSocial,
        nomeFantasia: formData.nomeFantasia,
        responsavelNome: formData.responsavelNome,
        responsavelCpf: formData.responsavelCpf,
        endereco: {
          cep: formData.cep,
          logradouro: formData.logradouro,
          numero: formData.numero,
          complemento: formData.complemento,
          bairro: formData.bairro,
          cidade: formData.cidade,
          estado: formData.estado,
        },
        taxRegime: formData.taxRegime,
        paymentMethod: formData.paymentMethod,
        sendWelcomeEmail: formData.sendWelcomeEmail,
      }

      await partnerAdminApi.clients.create(clientData)

      onSuccess()
      handleClose()
    } catch (error: any) {
      setSubmitError(error.response?.data?.message || error.message || 'Erro ao cadastrar clínica')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setCurrentStep(0)
    setFormData(initialFormData)
    setErrors({})
    setSubmitError(null)
    setCnpjStatus('idle')
    cnpjValidation.reset()
    onClose()
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-600 mb-4">
              <Building className="w-5 h-5" />
              <span className="font-medium">Dados da Empresa</span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CNPJ *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.cnpj}
                  onChange={(e) => handleCnpjChange(e.target.value)}
                  placeholder="00.000.000/0001-00"
                  maxLength={18}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:border-transparent ${
                    errors.cnpj || cnpjStatus === 'invalid' || cnpjStatus === 'exists'
                      ? 'border-red-500 focus:ring-red-500'
                      : cnpjStatus === 'valid'
                      ? 'border-green-500 focus:ring-green-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {cnpjStatus === 'validating' && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />}
                  {cnpjStatus === 'valid' && <CheckCircle className="w-5 h-5 text-green-500" />}
                  {(cnpjStatus === 'invalid' || cnpjStatus === 'exists') && <AlertCircle className="w-5 h-5 text-red-500" />}
                </div>
              </div>
              {errors.cnpj && <p className="mt-1 text-sm text-red-600">{errors.cnpj}</p>}
              {cnpjStatus === 'exists' && cnpjValidation.existsCheck?.client && (
                <p className="mt-1 text-sm text-red-600">
                  CNPJ já cadastrado: {cnpjValidation.existsCheck.client.name}
                </p>
              )}
              {cnpjStatus === 'valid' && (
                <p className="mt-1 text-sm text-green-600">CNPJ válido - dados preenchidos automaticamente</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome da Clínica/Consultório *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                placeholder="Ex: Clínica Saúde Total"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Razão Social
              </label>
              <input
                type="text"
                value={formData.razaoSocial}
                onChange={(e) => updateFormData('razaoSocial', e.target.value)}
                placeholder="Razão social da empresa"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Fantasia
              </label>
              <input
                type="text"
                value={formData.nomeFantasia}
                onChange={(e) => updateFormData('nomeFantasia', e.target.value)}
                placeholder="Nome fantasia"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )

      case 1:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-600 mb-4">
              <User className="w-5 h-5" />
              <span className="font-medium">Dados de Contato</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  placeholder="contato@clinica.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  placeholder="(11) 99999-9999"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Responsável *
              </label>
              <input
                type="text"
                value={formData.responsavelNome}
                onChange={(e) => updateFormData('responsavelNome', e.target.value)}
                placeholder="Nome completo do responsável"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.responsavelNome ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.responsavelNome && <p className="mt-1 text-sm text-red-600">{errors.responsavelNome}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CPF do Responsável
              </label>
              <input
                type="text"
                value={formData.responsavelCpf}
                onChange={(e) => updateFormData('responsavelCpf', e.target.value)}
                placeholder="000.000.000-00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-600 mb-4">
              <FileText className="w-5 h-5" />
              <span className="font-medium">Endereço</span>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CEP *
                </label>
                <input
                  type="text"
                  value={formData.cep}
                  onChange={(e) => updateFormData('cep', e.target.value)}
                  placeholder="00000-000"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.cep ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.cep && <p className="mt-1 text-sm text-red-600">{errors.cep}</p>}
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logradouro *
                </label>
                <input
                  type="text"
                  value={formData.logradouro}
                  onChange={(e) => updateFormData('logradouro', e.target.value)}
                  placeholder="Rua, Avenida, etc."
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.logradouro ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.logradouro && <p className="mt-1 text-sm text-red-600">{errors.logradouro}</p>}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número *
                </label>
                <input
                  type="text"
                  value={formData.numero}
                  onChange={(e) => updateFormData('numero', e.target.value)}
                  placeholder="123"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.numero ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.numero && <p className="mt-1 text-sm text-red-600">{errors.numero}</p>}
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Complemento
                </label>
                <input
                  type="text"
                  value={formData.complemento}
                  onChange={(e) => updateFormData('complemento', e.target.value)}
                  placeholder="Sala 101, Bloco A"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bairro
                </label>
                <input
                  type="text"
                  value={formData.bairro}
                  onChange={(e) => updateFormData('bairro', e.target.value)}
                  placeholder="Centro"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cidade *
                </label>
                <input
                  type="text"
                  value={formData.cidade}
                  onChange={(e) => updateFormData('cidade', e.target.value)}
                  placeholder="São Paulo"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.cidade ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.cidade && <p className="mt-1 text-sm text-red-600">{errors.cidade}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado *
                </label>
                <Select
                  value={formData.estado}
                  onChange={(e) => updateFormData('estado', e.target.value)}
                  options={[
                    { value: '', label: 'Selecione' },
                    { value: 'AC', label: 'AC' },
                    { value: 'AL', label: 'AL' },
                    { value: 'AP', label: 'AP' },
                    { value: 'AM', label: 'AM' },
                    { value: 'BA', label: 'BA' },
                    { value: 'CE', label: 'CE' },
                    { value: 'DF', label: 'DF' },
                    { value: 'ES', label: 'ES' },
                    { value: 'GO', label: 'GO' },
                    { value: 'MA', label: 'MA' },
                    { value: 'MT', label: 'MT' },
                    { value: 'MS', label: 'MS' },
                    { value: 'MG', label: 'MG' },
                    { value: 'PA', label: 'PA' },
                    { value: 'PB', label: 'PB' },
                    { value: 'PR', label: 'PR' },
                    { value: 'PE', label: 'PE' },
                    { value: 'PI', label: 'PI' },
                    { value: 'RJ', label: 'RJ' },
                    { value: 'RN', label: 'RN' },
                    { value: 'RS', label: 'RS' },
                    { value: 'RO', label: 'RO' },
                    { value: 'RR', label: 'RR' },
                    { value: 'SC', label: 'SC' },
                    { value: 'SP', label: 'SP' },
                    { value: 'SE', label: 'SE' },
                    { value: 'TO', label: 'TO' }
                  ]}
                  error={!!errors.estado}
                  fullWidth
                />
                {errors.estado && <p className="mt-1 text-sm text-red-600">{errors.estado}</p>}
              </div>
            </div>
          </div>
        )

      case 3:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-600 mb-4">
              <CreditCard className="w-5 h-5" />
              <span className="font-medium">Plano e Pagamento</span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Plano de Assinatura *
              </label>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: 'basic', label: 'Básico', price: 'R$ 197/mês', features: ['5 usuários', '100 documentos/mês'] },
                  { value: 'professional', label: 'Profissional', price: 'R$ 397/mês', features: ['15 usuários', '500 documentos/mês'], recommended: true },
                  { value: 'enterprise', label: 'Enterprise', price: 'R$ 997/mês', features: ['Ilimitado', 'Suporte prioritário'] },
                ].map((plan) => (
                  <button
                    key={plan.value}
                    type="button"
                    onClick={() => updateFormData('subscriptionPlan', plan.value as any)}
                    className={`relative p-4 border-2 rounded-lg text-left transition-all ${
                      formData.subscriptionPlan === plan.value
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {plan.recommended && (
                      <span className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                        Recomendado
                      </span>
                    )}
                    <div className="font-medium text-gray-900">{plan.label}</div>
                    <div className="text-lg font-bold text-blue-600 mt-1">{plan.price}</div>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      {plan.features.map((f, i) => (
                        <li key={i}>• {f}</li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor Mensal Cobrado *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R$</span>
                <input
                  type="text"
                  value={formData.monthlyFee}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9,]/g, '')
                    updateFormData('monthlyFee', value)
                  }}
                  placeholder="0,00"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.monthlyFee ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Valor que você cobra mensalmente desta clínica</p>
              {errors.monthlyFee && <p className="mt-1 text-sm text-red-600">{errors.monthlyFee}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Forma de Pagamento
              </label>
              <div className="flex gap-4">
                {[
                  { value: 'boleto', label: 'Boleto Bancário' },
                  { value: 'pix', label: 'PIX' },
                  { value: 'credit_card', label: 'Cartão de Crédito' },
                ].map((method) => (
                  <button
                    key={method.value}
                    type="button"
                    onClick={() => updateFormData('paymentMethod', method.value as any)}
                    className={`flex-1 p-3 border-2 rounded-lg text-center transition-all ${
                      formData.paymentMethod === method.value
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-sm font-medium">{method.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )

      case 4:
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-600 mb-4">
              <Settings className="w-5 h-5" />
              <span className="font-medium">Configurações Iniciais</span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Especialidade Médica *
              </label>
              <Select
                value={formData.speciality}
                onChange={(e) => updateFormData('speciality', e.target.value)}
                options={[
                  { value: '', label: 'Selecione a especialidade' },
                  { value: 'clinica_geral', label: 'Clínica Geral' },
                  { value: 'cardiologia', label: 'Cardiologia' },
                  { value: 'dermatologia', label: 'Dermatologia' },
                  { value: 'endocrinologia', label: 'Endocrinologia' },
                  { value: 'gastroenterologia', label: 'Gastroenterologia' },
                  { value: 'ginecologia', label: 'Ginecologia' },
                  { value: 'neurologia', label: 'Neurologia' },
                  { value: 'oftalmologia', label: 'Oftalmologia' },
                  { value: 'ortopedia', label: 'Ortopedia' },
                  { value: 'pediatria', label: 'Pediatria' },
                  { value: 'psiquiatria', label: 'Psiquiatria' },
                  { value: 'urologia', label: 'Urologia' },
                  { value: 'outra', label: 'Outra' }
                ]}
                error={!!errors.speciality}
                fullWidth
              />
              {errors.speciality && <p className="mt-1 text-sm text-red-600">{errors.speciality}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Regime Tributário
              </label>
              <Select
                value={formData.taxRegime}
                onChange={(e) => updateFormData('taxRegime', e.target.value)}
                options={[
                  { value: 'simples_nacional', label: 'Simples Nacional' },
                  { value: 'lucro_presumido', label: 'Lucro Presumido' },
                  { value: 'lucro_real', label: 'Lucro Real' },
                  { value: 'mei', label: 'MEI' }
                ]}
                fullWidth
              />
            </div>

            <div className="pt-4 border-t border-gray-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sendWelcomeEmail}
                  onChange={(e) => updateFormData('sendWelcomeEmail', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">Enviar email de boas-vindas</span>
                  <p className="text-xs text-gray-500">A clínica receberá um email com instruções de acesso</p>
                </div>
              </label>
            </div>

            {/* Summary */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Resumo do Cadastro</h4>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Clínica:</dt>
                  <dd className="font-medium">{formData.name}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">CNPJ:</dt>
                  <dd className="font-medium">{formData.cnpj}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Plano:</dt>
                  <dd className="font-medium capitalize">{formData.subscriptionPlan}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Valor Mensal:</dt>
                  <dd className="font-medium">R$ {formData.monthlyFee || '0,00'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Pagamento:</dt>
                  <dd className="font-medium capitalize">{formData.paymentMethod.replace('_', ' ')}</dd>
                </div>
              </dl>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Cadastrar Nova Clínica</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Stepper */}
          <Stepper
            steps={steps}
            currentStep={currentStep}
            allowClickNavigation={true}
            onStepClick={(step) => {
              // Only allow going back to completed steps
              if (step < currentStep) {
                setCurrentStep(step)
              }
            }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{submitError}</span>
            </div>
          )}

          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>

          <button
            onClick={handleNext}
            disabled={submitting || (currentStep === 0 && cnpjStatus === 'validating')}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Cadastrando...
              </>
            ) : currentStep === steps.length - 1 ? (
              <>
                <Check className="w-4 h-4" />
                Finalizar Cadastro
              </>
            ) : (
              <>
                Próximo
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ClientOnboardingWizard
